import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SortKey } from "./dashboard-table-helpers"
import type { DashboardUseCase, SortDirection } from "../../types/use-case.types"

import { Settings2, ThumbsUp } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SortableTableHeader } from "@/components/ui/sortable-table-header"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { useUseCasesStore } from "@/stores/use-cases-store"

import {
  COLUMNS,
  feasBadgeVariant,
  getSortValue,
  statusBadgeVariant,
  tenkBadgeVariant,
} from "./dashboard-table-helpers"
import { useVoteUseCase } from "../../hooks/use-vote-use-case"
import { formatNumber, formatUSD } from "../../utils/format-helpers"
import { QAScoreBadge } from "../fact-sheet/qa-review-dialog"

type DashboardTableProps = {
  useCases: DashboardUseCase[]
  showOwnerColumn?: boolean
}

export function DashboardTable({ useCases, showOwnerColumn }: DashboardTableProps) {
  const { t } = useTranslation()
  const { sortColumn, sortDirection, setSort, setSelectedUseCaseId, setActiveTab } =
    useUseCasesStore()
  const voteMutation = useVoteUseCase()
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    new Set(COLUMNS.map((c) => c.key))
  )

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortColumn === key) {
        setSort(key, sortDirection === "asc" ? "desc" : "asc")
      } else {
        setSort(key, "desc")
      }
    },
    [sortColumn, sortDirection, setSort]
  )

  const currentSort = useMemo(
    () => ({
      key: sortColumn as SortKey | null,
      direction: sortDirection as SortDirection | null,
    }),
    [sortColumn, sortDirection]
  )

  const sortedUseCases = useMemo(() => {
    if (!sortColumn) return useCases
    return [...useCases].sort((a, b) => {
      const aVal = getSortValue(a, sortColumn as SortKey)
      const bVal = getSortValue(b, sortColumn as SortKey)
      const cmp =
        typeof aVal === "string"
          ? aVal.localeCompare(bVal as string)
          : (aVal as number) - (bVal as number)
      return sortDirection === "asc" ? cmp : -cmp
    })
  }, [useCases, sortColumn, sortDirection])

  const totals = useMemo(() => {
    let investment = 0
    let opCost = 0
    let hours = 0
    let benefit = 0
    let roi = 0
    for (const uc of useCases) {
      investment += parseFloat(uc.investment) || 0
      opCost += uc.opCostYearly
      hours += uc.totalCapacityHours
      benefit += uc.annualBenefit
      roi += uc.roi12m
    }
    return { investment, opCost, hours, benefit, roi }
  }, [useCases])

  const hiddenCount =
    COLUMNS.filter((c) => !c.alwaysVisible).length -
    COLUMNS.filter((c) => !c.alwaysVisible && visibleColumns.has(c.key)).length

  const footerColSpan =
    (visibleColumns.has("votes") ? 1 : 0) +
    1 + // ucName (always visible)
    (showOwnerColumn ? 1 : 0) +
    (visibleColumns.has("sites") ? 1 : 0) +
    (visibleColumns.has("efSel") ? 1 : 0) +
    (visibleColumns.has("statusText") ? 1 : 0) +
    (visibleColumns.has("tenkCategory") ? 1 : 0)

  const footerTrailingColSpan =
    (visibleColumns.has("bpSel") ? 1 : 0) +
    (visibleColumns.has("feasV") ? 1 : 0) +
    (visibleColumns.has("qaScore") ? 1 : 0)

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline">
              <Settings2 className="mr-1 size-4" />
              {t("useCases.dashboard.columns")}
              {hiddenCount > 0 && (
                <span className="bg-muted ml-1 rounded px-1.5 text-xs">{hiddenCount} hidden</span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {COLUMNS.filter((c) => !c.alwaysVisible).map((col) => (
              <DropdownMenuCheckboxItem
                key={col.key}
                checked={visibleColumns.has(col.key)}
                onCheckedChange={(checked) => {
                  const next = new Set(visibleColumns)
                  if (checked) next.add(col.key)
                  else next.delete(col.key)
                  setVisibleColumns(next)
                }}
              >
                {col.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Table parentClassName="max-h-[600px]">
        <TableHeader className="bg-muted/50 sticky top-0">
          <TableRow>
            {visibleColumns.has("votes") && (
              <SortableTableHeader currentSort={currentSort} sortKey="votes" onSort={handleSort}>
                Votes
              </SortableTableHeader>
            )}
            <SortableTableHeader currentSort={currentSort} sortKey="ucName" onSort={handleSort}>
              {t("useCases.dashboard.table.useCase")}
            </SortableTableHeader>
            {showOwnerColumn && (
              <SortableTableHeader currentSort={currentSort} sortKey="ucName" onSort={handleSort}>
                Owner
              </SortableTableHeader>
            )}
            {visibleColumns.has("sites") && (
              <SortableTableHeader currentSort={currentSort} sortKey="sites" onSort={handleSort}>
                {t("useCases.dashboard.table.site")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("efSel") && (
              <SortableTableHeader currentSort={currentSort} sortKey="efSel" onSort={handleSort}>
                {t("useCases.dashboard.table.function")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("statusText") && (
              <SortableTableHeader
                currentSort={currentSort}
                sortKey="statusText"
                onSort={handleSort}
              >
                {t("useCases.dashboard.table.status")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("tenkCategory") && (
              <SortableTableHeader
                currentSort={currentSort}
                sortKey="tenkCategory"
                onSort={handleSort}
              >
                {t("useCases.dashboard.table.tenkRule")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("investment") && (
              <SortableTableHeader
                align="right"
                currentSort={currentSort}
                sortKey="investment"
                onSort={handleSort}
              >
                {t("useCases.dashboard.table.investment")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("opCostYearly") && (
              <SortableTableHeader
                align="right"
                currentSort={currentSort}
                sortKey="opCostYearly"
                onSort={handleSort}
              >
                {t("useCases.dashboard.table.opCost")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("totalCapacityHours") && (
              <SortableTableHeader
                align="right"
                currentSort={currentSort}
                sortKey="totalCapacityHours"
                onSort={handleSort}
              >
                {t("useCases.dashboard.table.hours")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("annualBenefit") && (
              <SortableTableHeader
                align="right"
                currentSort={currentSort}
                sortKey="annualBenefit"
                onSort={handleSort}
              >
                {t("useCases.dashboard.table.annualBenefit")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("roi12m") && (
              <SortableTableHeader
                align="right"
                currentSort={currentSort}
                sortKey="roi12m"
                onSort={handleSort}
              >
                {t("useCases.dashboard.table.roi")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("bpSel") && (
              <SortableTableHeader currentSort={currentSort} sortKey="bpSel" onSort={handleSort}>
                {t("useCases.dashboard.table.businessPain")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("feasV") && (
              <SortableTableHeader currentSort={currentSort} sortKey="feasV" onSort={handleSort}>
                {t("useCases.dashboard.table.feasibility")}
              </SortableTableHeader>
            )}
            {visibleColumns.has("qaScore") && (
              <SortableTableHeader currentSort={currentSort} sortKey="qaScore" onSort={handleSort}>
                QA Score
              </SortableTableHeader>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedUseCases.map((uc) => (
            <TableRow
              key={uc.id}
              className="hover:bg-accent/50 hover:text-accent-foreground cursor-pointer transition-colors"
              onClick={() => {
                setSelectedUseCaseId(uc.id)
                setActiveTab("factsheet")
              }}
            >
              {visibleColumns.has("votes") && (
                <TableCell className="px-4 py-2">
                  <button
                    className={cn(
                      "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors",
                      uc.hasVoted
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    title={
                      uc.hasVoted
                        ? t("useCases.dashboard.table.removeVote")
                        : t("useCases.dashboard.table.voteForUseCase")
                    }
                    onClick={(e) => {
                      e.stopPropagation()
                      voteMutation.mutate(uc.id)
                    }}
                  >
                    <ThumbsUp className={cn("size-3.5", uc.hasVoted && "fill-current")} />
                    <span className="tabular-nums">{uc.voteCount ?? 0}</span>
                  </button>
                </TableCell>
              )}
              <TableCell className="max-w-[200px] px-4 py-2">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium">{uc.ucName}</span>
                  {uc.hasUnviewedNotification && (
                    <span
                      className="border-foreground bg-primary inline-block size-2 rounded-full border"
                      title={t("useCases.dashboard.table.newInvitation")}
                    />
                  )}
                </div>
                {uc.sites.length > 1 && (
                  <span className="text-muted-foreground text-xs">
                    {t("useCases.dashboard.table.multiSites", { count: uc.sites.length })}
                  </span>
                )}
              </TableCell>
              {showOwnerColumn && (
                <TableCell className="px-4 py-2 text-xs">{uc.owner || "-"}</TableCell>
              )}
              {visibleColumns.has("sites") && (
                <TableCell className="px-4 py-2 text-xs">
                  {uc.sites.length === 1 ? uc.sites[0] : `${uc.sites.length} sites`}
                </TableCell>
              )}
              {visibleColumns.has("efSel") && (
                <TableCell className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {uc.efSel.map((fn) => (
                      <span key={fn} className="bg-muted rounded px-1.5 py-0.5 text-xs">
                        {fn}
                      </span>
                    ))}
                  </div>
                </TableCell>
              )}
              {visibleColumns.has("statusText") && (
                <TableCell className="px-4 py-2 whitespace-nowrap">
                  <Badge
                    className="w-fit shrink-0 whitespace-nowrap"
                    variant={
                      statusBadgeVariant(uc.statusText) as
                        | "success"
                        | "warning"
                        | "error"
                        | "secondary"
                    }
                  >
                    {uc.statusText}
                  </Badge>
                </TableCell>
              )}
              {visibleColumns.has("tenkCategory") && (
                <TableCell className="px-4 py-2 whitespace-nowrap">
                  <Badge
                    className="w-fit shrink-0 whitespace-nowrap"
                    variant={
                      tenkBadgeVariant(uc.tenkCategory) as
                        | "success"
                        | "warning"
                        | "error"
                        | "secondary"
                    }
                  >
                    {uc.tenkCategory}
                  </Badge>
                </TableCell>
              )}
              {visibleColumns.has("investment") && (
                <TableCell className="px-4 py-2 text-right tabular-nums">
                  {formatUSD(parseFloat(uc.investment) || 0)}
                </TableCell>
              )}
              {visibleColumns.has("opCostYearly") && (
                <TableCell className="px-4 py-2 text-right tabular-nums">
                  {formatUSD(uc.opCostYearly)}
                </TableCell>
              )}
              {visibleColumns.has("totalCapacityHours") && (
                <TableCell className="px-4 py-2 text-right tabular-nums">
                  {formatNumber(Math.round(uc.totalCapacityHours))}
                </TableCell>
              )}
              {visibleColumns.has("annualBenefit") && (
                <TableCell className="px-4 py-2 text-right tabular-nums">
                  {formatUSD(uc.annualBenefit)}
                </TableCell>
              )}
              {visibleColumns.has("roi12m") && (
                <TableCell
                  className={cn(
                    "px-4 py-2 text-right font-medium tabular-nums",
                    uc.roi12m >= 0 ? "text-success" : "text-destructive"
                  )}
                >
                  {formatUSD(uc.roi12m)}
                </TableCell>
              )}
              {visibleColumns.has("bpSel") && (
                <TableCell className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {uc.bpSel.map((bp) => (
                      <span key={bp} className="bg-muted rounded px-1.5 py-0.5 text-xs">
                        {bp}
                      </span>
                    ))}
                  </div>
                </TableCell>
              )}
              {visibleColumns.has("feasV") && (
                <TableCell className="px-4 py-2 whitespace-nowrap">
                  <Badge
                    className="w-fit shrink-0 whitespace-nowrap"
                    variant={
                      feasBadgeVariant(uc.feasV) as "success" | "warning" | "error" | "secondary"
                    }
                  >
                    {uc.feasLabel}
                  </Badge>
                </TableCell>
              )}
              {visibleColumns.has("qaScore") && (
                <TableCell className="px-4 py-2">
                  {uc.qaScore != null && uc.qaGrade ? (
                    <QAScoreBadge grade={uc.qaGrade} score={uc.qaScore} />
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow className="bg-muted font-semibold">
            <TableCell className="px-4 py-2" colSpan={footerColSpan}>
              {t("useCases.dashboard.table.total")} ({useCases.length})
            </TableCell>
            {visibleColumns.has("investment") && (
              <TableCell className="px-4 py-2 text-right tabular-nums">
                {formatUSD(totals.investment)}
              </TableCell>
            )}
            {visibleColumns.has("opCostYearly") && (
              <TableCell className="px-4 py-2 text-right tabular-nums">
                {formatUSD(totals.opCost)}
              </TableCell>
            )}
            {visibleColumns.has("totalCapacityHours") && (
              <TableCell className="px-4 py-2 text-right tabular-nums">
                {formatNumber(Math.round(totals.hours))}
              </TableCell>
            )}
            {visibleColumns.has("annualBenefit") && (
              <TableCell className="px-4 py-2 text-right tabular-nums">
                {formatUSD(totals.benefit)}
              </TableCell>
            )}
            {visibleColumns.has("roi12m") && (
              <TableCell
                className={cn(
                  "px-4 py-2 text-right tabular-nums",
                  totals.roi >= 0 ? "text-success" : "text-destructive"
                )}
              >
                {formatUSD(totals.roi)}
              </TableCell>
            )}
            {footerTrailingColSpan > 0 && (
              <TableCell className="px-4 py-2" colSpan={footerTrailingColSpan} />
            )}
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  )
}
